import React, { useState, useEffect } from 'react';
import { 
  Building2, Heart, Landmark, ShoppingCart, Factory,
  TrendingUp, TrendingDown, Users, DollarSign, Shield,
  Activity, CreditCard, FileText, AlertTriangle, CheckCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import BrandTitle from '../components/BrandTitle';

const IndustryIntelligence = () => {
  const { shouldShowData } = useDemoData();
  const [activeIndustry, setActiveIndustry] = useState('healthcare');
  const [data, setData] = useState(null);
  const [benchmarks, setBenchmarks] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const industries = [
    { id: 'healthcare', name: 'Healthcare', icon: Heart, color: 'text-red-500 bg-red-100' },
    { id: 'banking', name: 'Banking & Finance', icon: Landmark, color: 'text-emerald-500 bg-emerald-100' },
    { id: 'retail', name: 'Retail & E-commerce', icon: ShoppingCart, color: 'text-emerald-500 bg-emerald-100' }
  ];

  useEffect(() => {
    fetchIndustryData();
  }, [activeIndustry]);

  const fetchIndustryData = async () => {
    setLoading(true);
    try {
      let endpoint = '';
      switch (activeIndustry) {
        case 'healthcare':
          endpoint = '/api/industry/healthcare/hipaa-costs';
          break;
        case 'banking':
          endpoint = '/api/industry/banking/transaction-costs';
          break;
        case 'retail':
          endpoint = '/api/industry/retail/campaign-costs';
          break;
        default:
          endpoint = '/api/industry/healthcare/hipaa-costs';
      }

      const [dataResponse, benchmarkResponse] = await Promise.all([
        fetch(`${API_URL}${endpoint}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/benchmarks?industry=${activeIndustry === 'banking' ? 'financial_services' : activeIndustry}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const dataResult = await dataResponse.json();
      const benchmarkResult = await benchmarkResponse.json();
      
      setData(dataResult);
      setBenchmarks(benchmarkResult);
    } catch (error) {
      console.error('Error fetching industry data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderHealthcareContent = () => (
    <div className="space-y-6">
      {/* HIPAA Compliance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-5 h-5 text-green-500" />
            <span className="text-slate-400 dark:text-gray-400">Compliance Score</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{data?.compliance_score}/100</p>
          <p className="text-sm text-green-500">Audit Ready</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-5 h-5 text-emerald-500" />
            <span className="text-slate-400 dark:text-gray-400">HIPAA Costs</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            ${data?.total_hipaa_costs?.toLocaleString()}
          </p>
          <p className="text-sm text-slate-400 dark:text-gray-400">Monthly compliance spend</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-emerald-500" />
            <span className="text-slate-400 dark:text-gray-400">Cost per Patient</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            ${data?.per_patient_metrics?.average_cost_per_patient?.toFixed(2)}
          </p>
          <p className="text-sm text-slate-400 dark:text-gray-400">
            {data?.per_patient_metrics?.total_patients?.toLocaleString()} patients
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <Activity className="w-5 h-5 text-orange-500" />
            <span className="text-slate-400 dark:text-gray-400">Next Audit</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{data?.next_audit_date}</p>
          <p className="text-sm text-green-500">{data?.audit_readiness}</p>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">HIPAA Compliance Cost Breakdown</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data?.breakdown && Object.entries(data.breakdown).map(([key, value]) => (
            <div key={key} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <h4 className="font-medium text-gray-900 dark:text-white capitalize mb-2">
                {key.replace(/_/g, ' ')}
              </h4>
              <p className="text-2xl font-bold text-emerald-500">${value.cost?.toLocaleString()}</p>
              <div className="mt-2 space-y-1 text-sm text-slate-400 dark:text-gray-400">
                {value.encryption_type && <p>Encryption: {value.encryption_type}</p>}
                {value.log_retention_days && <p>Retention: {value.log_retention_days} days</p>}
                {value.mfa_enabled_users && <p>MFA Users: {value.mfa_enabled_users}</p>}
                {value.geo_redundant !== undefined && <p>Geo-redundant: {value.geo_redundant ? 'Yes' : 'No'}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Compliance Recommendations</h3>
        <div className="space-y-3">
          {data?.recommendations?.map((rec, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center gap-3">
                <AlertTriangle className={`w-5 h-5 ${rec.priority === 'high' ? 'text-red-500' : 'text-yellow-500'}`} />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{rec.action}</p>
                  <p className="text-sm text-slate-400 dark:text-gray-400">
                    +{rec.compliance_improvement} compliance points
                  </p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm ${rec.cost_impact > 0 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                {rec.cost_impact > 0 ? '+' : ''}{rec.cost_impact < 0 ? '-' : ''}${Math.abs(rec.cost_impact)}/mo
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBankingContent = () => (
    <div className="space-y-6">
      {/* Transaction Cost Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <CreditCard className="w-5 h-5 text-emerald-500" />
            <span className="text-slate-400 dark:text-gray-400">Total Transactions</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {data?.summary?.total_transactions?.toLocaleString()}
          </p>
          <p className="text-sm text-slate-400 dark:text-gray-400">This month</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-5 h-5 text-green-500" />
            <span className="text-slate-400 dark:text-gray-400">Infrastructure Cost</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            ${data?.summary?.total_infrastructure_cost?.toLocaleString()}
          </p>
          <p className="text-sm text-slate-400 dark:text-gray-400">Monthly</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <Activity className="w-5 h-5 text-emerald-500" />
            <span className="text-slate-400 dark:text-gray-400">Cost per Transaction</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            ${data?.summary?.blended_cost_per_transaction?.toFixed(4)}
          </p>
          <p className="text-sm text-slate-400 dark:text-gray-400">Blended average</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-5 h-5 text-orange-500" />
            <span className="text-slate-400 dark:text-gray-400">Annual Projection</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            ${(data?.summary?.projected_annual_cost / 1000)?.toFixed(0)}K
          </p>
          <p className="text-sm text-slate-400 dark:text-gray-400">Projected</p>
        </div>
      </div>

      {/* Transaction Types */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Cost by Transaction Type</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-slate-400 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <th className="pb-3">Transaction Type</th>
                <th className="pb-3">Volume</th>
                <th className="pb-3">Cost/Transaction</th>
                <th className="pb-3">Total Cost</th>
                <th className="pb-3">Trend</th>
              </tr>
            </thead>
            <tbody>
              {data?.transaction_types?.map((tx, index) => (
                <tr key={index} className="border-b border-gray-100 dark:border-gray-700/50">
                  <td className="py-4 font-medium text-gray-900 dark:text-white">{tx.type}</td>
                  <td className="py-4 text-slate-400 dark:text-gray-400">{tx.volume?.toLocaleString()}</td>
                  <td className="py-4 text-slate-400 dark:text-gray-400">${tx.cost_per?.toFixed(4)}</td>
                  <td className="py-4 font-medium text-gray-900 dark:text-white">${tx.total_cost?.toLocaleString()}</td>
                  <td className="py-4">
                    <span className={`flex items-center gap-1 ${tx.trend_vs_last_month >= 0 ? 'text-red-500' : 'text-green-500'}`}>
                      {tx.trend_vs_last_month >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                      {Math.abs(tx.trend_vs_last_month)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Optimization Opportunities */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Optimization Opportunities</h3>
        <div className="space-y-3">
          {data?.optimization_opportunities?.map((opp, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-green-50 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{opp.opportunity}</p>
                  <p className="text-sm text-slate-400 dark:text-gray-400">
                    Implementation effort: {opp.implementation_effort}
                  </p>
                </div>
              </div>
              <span className="px-4 py-2 bg-green-500 text-white rounded-lg font-bold">
                Save ${opp.potential_savings?.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderRetailContent = () => (
    <div className="space-y-6">
      {/* Campaign Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <ShoppingCart className="w-5 h-5 text-emerald-500" />
            <span className="text-slate-400 dark:text-gray-400">Campaigns</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {data?.campaigns?.length || 0}
          </p>
          <p className="text-sm text-slate-400 dark:text-gray-400">Active campaigns</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-5 h-5 text-emerald-500" />
            <span className="text-slate-400 dark:text-gray-400">Cloud Cost</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            ${data?.summary?.total_campaign_cloud_cost?.toLocaleString()}
          </p>
          <p className="text-sm text-slate-400 dark:text-gray-400">Campaign infrastructure</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-green-500" />
            <span className="text-slate-400 dark:text-gray-400">Total Conversions</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {data?.summary?.total_conversions?.toLocaleString()}
          </p>
          <p className="text-sm text-slate-400 dark:text-gray-400">All campaigns</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-5 h-5 text-orange-500" />
            <span className="text-slate-400 dark:text-gray-400">Avg ROAS</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {data?.summary?.average_roas?.toFixed(1)}x
          </p>
          <p className="text-sm text-slate-400 dark:text-gray-400">Return on ad spend</p>
        </div>
      </div>

      {/* Campaign Details */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Campaign Cost Attribution</h3>
        <div className="space-y-4">
          {data?.campaigns?.map((campaign, index) => (
            <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">{campaign.name}</h4>
                  <span className="text-sm text-slate-400 dark:text-gray-400 capitalize">{campaign.type}</span>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm ${campaign.roas >= 3 ? 'bg-green-100 text-green-600' : campaign.roas >= 2 ? 'bg-emerald-100 text-emerald-600' : 'bg-yellow-100 text-yellow-600'}`}>
                  {campaign.roas?.toFixed(1)}x ROAS
                </span>
              </div>
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-slate-400 dark:text-gray-400">Budget</p>
                  <p className="font-medium text-gray-900 dark:text-white">${campaign.budget?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-slate-400 dark:text-gray-400">Cloud Cost</p>
                  <p className="font-medium text-emerald-500">${campaign.cloud_cost?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-slate-400 dark:text-gray-400">Conversions</p>
                  <p className="font-medium text-gray-900 dark:text-white">{campaign.conversions?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-slate-400 dark:text-gray-400">Revenue</p>
                  <p className="font-medium text-green-500">${campaign.revenue?.toLocaleString()}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (!shouldShowData) return <EmptyDataState title="Industry Intelligence" description="Connect your cloud accounts to benchmark costs against industry peers." />;
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="industry-intelligence-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white"><BrandTitle>Industry Intelligence</BrandTitle></h1>
          <p className="text-slate-400 dark:text-gray-400 mt-1">
            Industry-specific cost insights that no other tool provides
          </p>
        </div>
        <div className="bg-indigo-500 bg-gradient-to-r from-indigo-500 to-cyan-500 text-white px-4 py-2 rounded-full text-sm font-medium">
          Exclusive Feature
        </div>
      </div>

      {/* Industry Tabs */}
      <div className="flex gap-4">
        {industries.map((industry) => {
          const Icon = industry.icon;
          return (
            <button
              key={industry.id}
              onClick={() => setActiveIndustry(industry.id)}
              className={`flex items-center gap-3 px-6 py-3 rounded-xl font-medium transition-all ${
                activeIndustry === industry.id
                  ? 'bg-white dark:bg-gray-800 shadow-lg text-gray-900 dark:text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-slate-400 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              data-testid={`industry-tab-${industry.id}`}
            >
              <div className={`p-2 rounded-lg ${industry.color}`}>
                <Icon className="w-5 h-5" />
              </div>
              {industry.name}
            </button>
          );
        })}
      </div>

      {/* Industry Benchmarks */}
      {benchmarks && (
        <div className="bg-blue-500 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl p-6 text-white">
          <h3 className="text-lg font-bold mb-4">Industry Benchmark Comparison</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-white/80">Your Cloud % of Revenue</p>
              <p className="text-3xl font-bold">{benchmarks.your_metrics?.cloud_percent_of_revenue}%</p>
              <p className="text-sm text-white/60">Industry avg: {benchmarks.industry_benchmarks?.cloud_percent_of_revenue}%</p>
            </div>
            <div>
              <p className="text-white/80">Cost per Employee</p>
              <p className="text-3xl font-bold">${benchmarks.your_metrics?.cost_per_employee}</p>
              <p className="text-sm text-white/60">Industry avg: ${benchmarks.industry_benchmarks?.cost_per_employee}</p>
            </div>
            <div>
              <p className="text-white/80">Your Percentile</p>
              <p className="text-3xl font-bold">Top {100 - benchmarks.comparison?.percentile}%</p>
              <p className="text-sm text-white/60">vs. industry peers</p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>
      ) : (
        <>
          {activeIndustry === 'healthcare' && renderHealthcareContent()}
          {activeIndustry === 'banking' && renderBankingContent()}
          {activeIndustry === 'retail' && renderRetailContent()}
        </>
      )}
    </div>
  );
};

export default IndustryIntelligence;
